import { Avatar, Flex, Icon, IconButton, Input, InputGroup, InputLeftElement, useColorMode } from '@chakra-ui/core';
import { useObserver } from 'mobx-react';
import React, { useCallback, useEffect } from 'react';
import { MdPowerSettingsNew } from 'react-icons/md';

import useDebounce from '../hooks/useDebounce';
import { useStores } from '../hooks/useStores';
import { IUserModel } from '../stores/models/UserModel';

interface IHeaderProps {
    profile: IUserModel;
    signOut: () => void;
}

function useStoreData() {
    const { store } = useStores();
    return useObserver(() => ({
        setSearchTerm: store.search.setSearchTerm,
        searchTerm: store.search.searchTerm,
        cleanResults: store.search.cleanResults,
        search: store.search.search,
        toggleBrowseMusicPanel: store.config.toggleBrowseMusicPanel,
        isBrowseMusicPanelOpen: store.config.isBrowseMusicPanelOpen,
        currentTrackColors: store.config.currentTrackColors,
    }));
}

const Header: React.FC<IHeaderProps> = ({ profile, signOut }) => {
    const { colorMode } = useColorMode();
    const {
        setSearchTerm,
        searchTerm,
        search,
        cleanResults,
        isBrowseMusicPanelOpen,
        toggleBrowseMusicPanel,
        currentTrackColors,
    } = useStoreData();
    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const color = { light: 'gray.800', dark: 'gray.100' };

    const inputSearchHandler = useCallback(
        (event: React.FormEvent) => {
            const newSearchTerm = (event.target as HTMLInputElement).value;
            setSearchTerm(newSearchTerm);
        },
        [setSearchTerm]
    );

    const inputFocusHandler = useCallback(() => {
        if (!isBrowseMusicPanelOpen) {
            toggleBrowseMusicPanel();
        }
    }, [isBrowseMusicPanelOpen, toggleBrowseMusicPanel]);

    useEffect(() => {
        if (debouncedSearchTerm) {
            cleanResults();
            search(debouncedSearchTerm);
            if (!isBrowseMusicPanelOpen) {
                toggleBrowseMusicPanel();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchTerm]);

    return (
        <Flex w="100%" justifyContent="space-between" alignItems="center">
            <InputGroup flex={1} maxW={['100%', '400px', '400px']}>
                {/* eslint-disable-next-line react/no-children-prop */}
                <InputLeftElement children={<Icon name="search-2" color={currentTrackColors[1]} />} />
                <Input
                    onEmptied={inputSearchHandler}
                    onChange={inputSearchHandler}
                    onFocus={inputFocusHandler}
                    value={searchTerm}
                    w="100%"
                    size="lg"
                    placeholder="Browse music"
                    variant="filled"
                    type="search"
                    transition="all .3s"
                    backgroundColor="rgba(0,0,0,.2)"
                    _placeholder={{
                        color: currentTrackColors[1],
                    }}
                />
            </InputGroup>
            <Flex alignItems="center">
                <Avatar m="0 .5rem" size="md" name={profile.displayName} src={profile.photoURL}></Avatar>
                <IconButton
                    size="lg"
                    variant="ghost"
                    fontSize="30px"
                    color={color[colorMode]}
                    onClick={signOut}
                    aria-label="Sign out"
                    icon={MdPowerSettingsNew}
                />
            </Flex>
        </Flex>
    );
};

export default Header;
